import React, { useCallback, useEffect, useMemo, useState } from "react";
import pdfIcon from "../../assets/images/pdf.png";
import { useNavigate, useParams } from "react-router-dom";
import { EXLDownloadIcon } from "../../assets/Icons";
import axios from "axios";
import { useSelector } from "react-redux";
import CheckList from "../CheckList";
import ResponseList from "../ResponseList";
import { generateExcelForUserResponse } from "../../utils/excel";
import moment from "moment";
import EntityInfoV2View from "../common/EntityInfoV2View";
import { countNegativeResponses, countPositiveResponses } from "../../utils/util";
import QuizOutlinedIcon from "@mui/icons-material/QuizOutlined";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
const MachineInfoV2 = () => {
  const navigate = useNavigate();
  const { uuid } = useParams();
  const userData = useSelector((state) => state.userSlice);
  const configData = useSelector((state) => state.configSlice);
  const [machineloading, setMachineLoading] = useState(true);
  const [tabActiveKey, setTabActiveKey] = useState("checklist");
  const [machineDetails, setMachineDetails] = useState({});
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [downloadAllNegativeResponsesZip, setDownloadAllNegativeResponsesZip] = useState(() => () => {});
  const [responseLength, setResponseLenght] = useState(0);
  const [questionLength, setQestionLength] = useState(0);
  const [visibleGroups, setVisibleGroups] = useState([]);
  const [hiddenGroups, setHiddenGroups] = useState([]);
  const [userResponse, setUserResponse] = useState([]);
  const [questionMapping, setQuestionMapping] = useState({});
  const [tabActions, setTabActions] = useState(null);
  const [positiveResponseCount, setPositiveResponseCount] = useState(0);
  const [negativeResponseCount, setNegativeResponseCount] = useState(0);

  const updateResponseStats = useCallback((responses = []) => {
    setPositiveResponseCount(countPositiveResponses(responses));
    setNegativeResponseCount(countNegativeResponses(responses));
  }, []);

  const fetchOverviewStats = useCallback(async () => {
    if (!uuid) return;
    try {
      const headers = {
        Authorization: `Bearer ${userData.token}`,
        "Content-Type": "application/json",
      };
      const [questionsRes, responsesRes] = await Promise.all([
        axios.get(`${configData.config.API_URL}/api/machine/questions/all/${uuid}`, { headers }),
        axios.get(`${configData.config.API_URL}/api/machine/quiz/responses/${uuid}`, { headers }),
      ]);
      const questions = questionsRes.data?.data || [];
      const responses = responsesRes.data?.data || [];
      setQestionLength(questions.length);
      setResponseLenght(responses.length);
      updateResponseStats(responses);
    } catch (error) {
      console.log("error", error);
    }
  }, [configData.config.API_URL, updateResponseStats, userData.token, uuid]);

  const fetchMachineDetails = async () => {
    try {
      const { data } = await axios.get(`${configData.config.API_URL}/api/machine/details/${uuid}`, {
        headers: {
          Authorization: `Bearer ${userData.token}`,
          "Content-Type": "application/json",
        },
      });
      setMachineDetails(data.data);
      getGroupDetails(data.data.groups);
      setMachineLoading(false);
    } catch (error) {
      console.log("error", error);
    }
  };

  const getGroupDetails = async (groups) => {
    try {
      const {
        data: { data: groupData },
      } = await axios.post(
        `${configData.config.UNO_URL}/api/user/bulk/group/details`,
        { groupIds: groups },
        {
          headers: {
            Authorization: `Bearer ${userData.token}`,
            "Content-Type": "application/json",
          },
        },
      );
      setVisibleGroups(groupData.slice(0, 2));
      if (groupData.length > 2) setHiddenGroups(groupData.slice(2));
    } catch (error) {
      console.log("error", error);
    }
  };

  const generateUserListExcel = () => {
    const staticHeaders =
      machineDetails?.data?.machineType === "Electric Pallet Jack"
        ? [
            "S.No.",
            "User",
            "Designation",
            "Associate Name",
            "Electric Pallet Jack (EPJ) ID",
            "Date & Time",
            "Health Status",
          ]
        : ["S.No.", "User", "Designation", "Date & Time", "Health Status"];

    const questionAndIdArray = [...staticHeaders];
    Object.entries(questionMapping || {}).forEach(([key, value]) => {
      questionAndIdArray.push(value);
      questionAndIdArray.push(key);
    });

    const isEPJ = machineDetails?.data?.machineType === "Electric Pallet Jack";
    const data = userResponse?.map((item, idx) => {
      const epjUserData = item?.data[0] || {};
      const sheetData = {
        "S.No.": idx + 1,
        User: item?.name || "",
        Designation: item?.designation || "",
        ...(isEPJ && {
          "Associate Name": epjUserData?.username || "",
          "Electric Pallet Jack (EPJ) ID": epjUserData?.machineId || "",
        }),
        "Date & Time": `${moment(item?.createdAt).format("DD-MM-YYYY")} | ${moment(item?.createdAt).format("HH:mm")}`,
        "Health Status": item?.health || "",
      };

      Object.entries(questionMapping || {}).forEach(([key, value]) => {
        sheetData[value] = 0;
        sheetData[key] = "";
      });

      item.data?.forEach((res) => {
        if (questionMapping[res.questionId]) {
          let answerValue;
          if (res.answer?.toLowerCase() === "yes") answerValue = 1;
          else if (res.answer?.toLowerCase() === "no" || res.answer?.toLowerCase() === "n/a") answerValue = 0;
          else answerValue = res.answer || 0;

          sheetData[questionMapping[res.questionId]] = answerValue;
          sheetData[res.questionId] =
            `Attachment : ${res.attachments?.[0] || "No Attachment"} || Comment : ${res.comment || "No Comments"}` ||
            "";
        }
      });

      return sheetData;
    });

    generateExcelForUserResponse(
      "User_List_Response",
      data,
      questionAndIdArray.length - staticHeaders.length,
      questionAndIdArray,
      questionAndIdArray,
      staticHeaders.length,
    );
  };

  useEffect(() => {
    fetchMachineDetails();
    fetchOverviewStats();
  }, [tabActiveKey, fetchOverviewStats]);

  useEffect(() => {
    if (tabActiveKey !== "checklist") setTabActions(null);
  }, [tabActiveKey]);

  const kpiCards = useMemo(
    () => [
      {
        key: "questions",
        icon: QuizOutlinedIcon,
        tone: "red",
        value: questionLength,
        label: "Total Questions",
        subtitle: "Total questions in this machine",
      },
      {
        key: "responses",
        icon: GroupsOutlinedIcon,
        tone: "green",
        value: responseLength,
        label: "User response received",
        subtitle: "Total responses submitted",
      },
      {
        key: "positive",
        icon: CheckCircleOutlineIcon,
        tone: "blue",
        value: positiveResponseCount,
        label: "Total Positive Response",
        subtitle: "Responses with 100% health status",
      },
      {
        key: "negative",
        icon: CancelOutlinedIcon,
        tone: "orange",
        value: negativeResponseCount,
        label: "Total Negative Response",
        subtitle: "Responses below 100% health status",
      },
    ],
    [negativeResponseCount, positiveResponseCount, questionLength, responseLength],
  );

  const tabs = useMemo(() => {
    const items = [];
    if (userData?.user?.permissions?.includes("MACHINE.QUESTIONS")) {
      items.push({ key: "checklist", label: "Questionnaire" });
    }
    if (userData?.user?.permissions?.includes("MACHINE.RESPONSES")) {
      items.push({ key: "response", label: "User Response" });
    }
    return items;
  }, [userData?.user?.permissions]);

  const responseTabActions =
    tabActiveKey === "response" ? (
      <>
        <button
          type="button"
          className="entity-info-v2__btn-outline"
          onClick={async () => {
            try {
              setDownloadingZip(true);
              await downloadAllNegativeResponsesZip();
            } finally {
              setDownloadingZip(false);
            }
          }}
          disabled={downloadingZip}
          title="Download All Negative PDFs"
        >
          {downloadingZip ? "..." : <img src={pdfIcon} alt="PDF" style={{ width: 18, height: 18 }} />}
        </button>
        <button
          type="button"
          className="entity-info-v2__btn-outline entity-info-v2__btn-icon entity-info-v2__btn-excel"
          onClick={generateUserListExcel}
          title="Download Excel"
        >
          <EXLDownloadIcon />
        </button>
      </>
    ) : null;

  return (
    <EntityInfoV2View
      navigate={navigate}
      loading={machineloading}
      entityName={machineDetails.name}
      editPath={`/machine-list/${uuid}`}
      thumbnail={machineDetails.thumbnail}
      responseCount={responseLength}
      questionCount={questionLength}
      serialNo={machineDetails?.data?.serialNo}
      entityIdLabel="Machine ID"
      entityId={machineDetails?.data?.machineId}
      visibleGroups={visibleGroups}
      hiddenGroups={hiddenGroups}
      tags={machineDetails?.tags}
      kpiCards={kpiCards}
      tabs={tabs}
      activeTab={tabActiveKey}
      onTabChange={setTabActiveKey}
      tabActions={tabActiveKey === "checklist" ? tabActions : responseTabActions}
    >
      {tabActiveKey === "checklist" && userData?.user?.permissions?.includes("MACHINE.QUESTIONS.VIEW") && (
        <CheckList
          setQestionLength={setQestionLength}
          setQuestionMapping={setQuestionMapping}
          setTabActions={setTabActions}
        />
      )}
      {tabActiveKey === "response" && userData?.user?.permissions?.includes("MACHINE.RESPONSES") && (
        <ResponseList
          variant="v2"
          setResponseLenght={setResponseLenght}
          setUserResponse={setUserResponse}
          onResponsesLoaded={updateResponseStats}
          machineDetails={machineDetails}
          setDownloadAllNegativeResponsesZip={setDownloadAllNegativeResponsesZip}
        />
      )}
    </EntityInfoV2View>
  );
};

export default MachineInfoV2;
